import moment from 'moment';
import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { getCurrentPosition, checkPermissions } from '../../utils/geolocation';
import socket from '../../utils/socket';
import { CLOCKING_TYPES } from '../../constants/clocking-types';

const initialState = {
  isLoading: false,
  isLoadingClockingCode: false,
  isLoadingSettings: false,
  error: false,
  code: null,
  isDevicePaired: false,
  clockingDeviceSites: [],
  clockingDeviceSite: {},
  clockingDevices: [],
  entity: null,
  type: null,
  clockingShifts: [],
  password: null
};

const slice = createSlice({
  name: 'clockings',
  initialState,
  reducers: {
    // LOADING
    startLoading: (state, action) => {
      const { loadingKey } = action.payload; // ex: 'isLoading', 'isLoadingCounters', etc.
      state[loadingKey] = true;
    },
    stopLoading: (state, action) => {
      const { loadingKey } = action.payload; // ex: 'isLoading', 'isLoadingCounters', etc.
      state[loadingKey] = false;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      console.error(state.error);
    },

    setError(state, action) {
      state.error = action.payload;
    },

    postDeviceAccess(state, action) {
      state.code = action.payload.code;
      state.isLoading = false;
    },

    postDevice(state) {
      state.error = false;
      state.isDevicePaired = true;
      state.isLoading = false;
    },

    postDeviceSettings(state) {
      state.error = false;
      state.isLoading = false;
    },

    getClockingDevices(state, action) {
      state.clockingDevices = action.payload.clockingDevices;
      state.isLoading = false;
    },

    getClockingDeviceSites(state, action) {
      const { clockingDeviceSites } = action.payload;

      let siteClockingIdx = localStorage.getItem('siteClockingIdx') || 0;
      if (!clockingDeviceSites[siteClockingIdx]) {
        siteClockingIdx = 0;
        localStorage.setItem('siteClockingIdx', 0);
      }

      state.clockingDeviceSites = clockingDeviceSites;
      state.clockingDeviceSite = clockingDeviceSites[siteClockingIdx] || {};
      state.isLoading = false;
    },

    getClockingDeviceShifts(state, action) {
      const { tomorrowShifts, todayShifts, yesterdayShifts, siteName } = action.payload;
      state.clockingShifts = { tomorrowShifts, todayShifts, yesterdayShifts, siteName };
      state.isLoading = false;
    },

    linkClockingDevices(state, action) {
      const { clockingDevice } = action.payload;

      // Comète On Time
      if (window.env.isClockingApp) {
        state.clockingDeviceSites.push(clockingDevice);
        state.clockingDeviceSite = clockingDevice;
        localStorage.setItem('siteClockingIdx', state.clockingDeviceSites.length - 1);
      } else {
        state.clockingDevices.push(clockingDevice);
      }
      state.isLoading = false;
    },

    unlinkClockingDevices(state, action) {
      const { siteClockingDeviceId } = action.payload;

      // Comète On Time
      if (window.env.isClockingApp) {
        const idx = state.clockingDeviceSites.findIndex((el) => el.siteClockingDeviceId === siteClockingDeviceId);
        state.clockingDeviceSites.splice(idx, 1);

        localStorage.setItem('siteClockingIdx', 0);
        const [firstSite] = state.clockingDeviceSites;
        if (firstSite) {
          state.clockingDeviceSite = firstSite;
        }
      } else {
        // Comète Link
        const idx = state.clockingDevices.findIndex((el) => el.siteClockingDeviceId === siteClockingDeviceId);
        state.clockingDevices.splice(idx, 1);
      }
      state.isLoading = false;
    },

    clockingInAndOut(state, action) {
      state.type = action.payload.type ?? 'resource';
      state.entity = action.payload.entity ?? action.payload.resource;
      state.isLoading = false;
    },

    setClockingShifts(state, action) {
      if (!action.payload.clockingShifts.length) {
        state.error = { code: 'NO_CLOCKING' };
      } else {
        state.clockingShifts = action.payload.clockingShifts;
        state.error = null;
      }
      state.isLoading = false;
    },

    clearClockingShifts(state) {
      state.clockingShifts = initialState.clockingShifts;
    },

    resetDeviceAccess(state) {
      state.code = null;
    },

    logoutClocking(state) {
      state.type = {};
      state.entity = {};
      state.clockingShifts = [];
      state.error = null;
    },

    setClockingDeviceSite(state, action) {
      state.clockingDeviceSite = action.payload;
    },

    setBreaks(state, action) {
      const { shiftId, breaks } = action.payload;

      if (!state.clockingShifts?.length) {
        return;
      }

      const idx = state.clockingShifts.findIndex(({ id }) => id === shiftId);
      state.clockingShifts[idx].breaks = breaks;
    },

    updateClockingShifts(state, action) {
      const initialShiftsArray = state.clockingShifts;
      const updatedShift = action.payload;
      const updatedShiftsArray = _.map(initialShiftsArray, (shift) => {
        if (shift.id === updatedShift.id) {
          return _.merge({}, shift, updatedShift);
        }
        return shift;
      });
      state.clockingShifts = updatedShiftsArray;
    },

    verifyAccess(state) {
      state.error = null;
    },

    setPassword(state, action) {
      state.password = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  clearClockingShifts,
  logoutClocking,
  resetDeviceAccess,
  setBreaks,
  setError,
  setClockingDeviceSite,
  updateClockingShifts,
  setPassword
} = slice.actions;

export function postDeviceAccess() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoading' }));
    try {
      let position = await getCurrentPosition();
      if (!position) {
        position = { latitude: null, longitude: null };
      }

      const { uuid, platform, deviceType, deviceName, deviceModel, deviceVendor, osName, browser } = window.env;

      const result = await socket.post(`/clockings/devices/access`, {
        uuid,
        platform,
        type: deviceType,
        deviceName,
        model: deviceModel,
        vendor: deviceVendor,
        os: osName,
        browser,
        socketId: socket.socket.io.id,
        position: { latitude: position?.latitude, longitude: position?.longitude }
      });

      dispatch(slice.actions.postDeviceAccess(result));
    } catch (err) {
      dispatch(slice.actions.hasError(err));
    }
  };
}

export function postDevice(siteId, { clockingByCode, clockingByNfc, shiftsVisibilityMode, isResourcesVisible, mode } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoadingSettings' }));
    try {
      const geolocPerms = await checkPermissions();
      let position = { latitude: null, longitude: null };
      if (geolocPerms) {
        position = await getCurrentPosition();
      }

      const { uuid, platform, deviceType, deviceName, deviceModel, deviceVendor, osName, browser } = window.env;

      await socket.post('/clockings/devices', {
        siteId,
        uuid,
        platform,
        type: deviceType,
        deviceName,
        model: deviceModel,
        vendor: deviceVendor,
        os: osName,
        browser,
        clockingByCode,
        clockingByNfc,
        shiftsVisibilityMode,
        isResourcesVisible,
        mode,
        position: {
          latitude: position?.latitude,
          longitude: position?.longitude
        }
      });

      dispatch(slice.actions.postDevice());
      return true;
    } catch (err) {
      dispatch(slice.actions.hasError(err));
      return false;
    } finally {
      dispatch(slice.actions.stopLoading({ loadingKey: 'isLoadingSettings' }));
    }
  };
}

export function postDeviceSettings({ clockingByCode, clockingByNfc, shiftsVisibilityMode, isResourcesVisible }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoadingSettings' }));
    try {
      const { uuid } = window.env;

      await socket.post('/clockings/settings', {
        uuid,
        clockingByCode,
        clockingByNfc,
        shiftsVisibilityMode,
        isResourcesVisible
      });

      dispatch(slice.actions.postDeviceSettings());
      return true;
    } catch (err) {
      dispatch(slice.actions.hasError(err));
      return false;
    } finally {
      dispatch(slice.actions.stopLoading({ loadingKey: 'isLoadingSettings' }));
    }
  };
}

export function getClockingDevices(siteId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoading' }));
    try {
      const result = await socket.get('/clockings/devices', {
        siteId
      });

      dispatch(slice.actions.getClockingDevices(result));
    } catch (err) {
      dispatch(slice.actions.hasError(err));
    }
  };
}

export function unlinkClockingDevices(siteClockingDeviceId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoading' }));
    try {
      await socket.delete('/clockings/site_device', {
        siteClockingDeviceId
      });

      dispatch(slice.actions.unlinkClockingDevices({ siteClockingDeviceId }));
    } catch (err) {
      dispatch(slice.actions.hasError(err));
    }
  };
}

export function linkClockingDevices({ device, siteId }, callback = null) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoading' }));
    try {
      const result = await socket.post('/clockings/site_device', {
        device,
        siteId
      });

      if (callback) {
        callback(result);
      }
      dispatch(slice.actions.linkClockingDevices(result));
    } catch (err) {
      dispatch(slice.actions.hasError(err));
    }
  };
}

export function getClockingDeviceSites({ uuid }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoading' }));
    try {
      const result = await socket.get('/clockings/device_sites', {
        uuid
      });

      dispatch(slice.actions.getClockingDeviceSites(result));

      const { clockingDeviceSites } = result;
      let siteClockingIdx = localStorage.getItem('siteClockingIdx') || 0;
      if (!clockingDeviceSites[siteClockingIdx]) {
        siteClockingIdx = 0;
      }
      const firstSite = clockingDeviceSites[siteClockingIdx];
      if (firstSite) {
        dispatch(getClockingDevices(firstSite.id));
      }
    } catch (err) {
      dispatch(slice.actions.hasError(err));
    }
  };
}

export function clockingInAndOut({ password = null, resourceId = null, resourceNfcUid = null }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoading' }));
    try {
      const result = await socket.get('/clockings/login', {
        password,
        resourceId,
        resourceNfcUid
      });

      // Le proxy webservice relaie parfois un `success: true` même quand l'API
      // Laravel renvoie `success: false` avec un code d'erreur (ex. WARN_INCORRECT_CODE).
      // En réponse de succès, le payload n'expose pas de `code` — sa présence est
      // donc un signal fiable d'erreur applicative.
      if (result?.code) {
        dispatch(slice.actions.hasError(result));
        return;
      }

      dispatch(slice.actions.clockingInAndOut(result));
    } catch (err) {
      dispatch(slice.actions.hasError(err));
    }
  };
}

export function sendClockingPassword(email) {
  return socket.get('/clockings/sendPassword', {
    email
  });
}

export function fetchClockingShifts({ resourceId, siteIds, companyId }) {
  // We take the timezone of the device, ignoring the cached infos
  const timezone = moment.tz.guess(true);
  const datetime = moment().tz(timezone).format('YYYY-MM-DD HH:mm:ss');

  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoading' }));
    try {
      const result = await socket.get('/schedules/clockingShifts', {
        resourceId,
        companyId,
        siteIds,
        datetime
      });

      dispatch(slice.actions.setClockingShifts({ ...result }));
    } catch (err) {
      dispatch(slice.actions.hasError(err));
    }
  };
}

export function verifyAccess({ email, password }, callback) {
  return async () => {
    try {
      const result = await socket.get('/verifyAccessClockingApp', {
        email,
        password
      });
      callback(result);
    } catch (err) {
      callback(err);
    }
  };
}

export function deleteClocking({ shiftId, type = CLOCKING_TYPES.PRE_CLOCKING, callback }) {
  return async () => {
    try {
      const result = await socket.delete('/clockings', {
        shiftId,
        type
      });
      callback(result);
    } catch (err) {
      callback(err);
    }
  };
}

export function getClockingDeviceShifts(password, siteClockingDeviceId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoadingClockingCode' }));
    try {
      const result = await socket.get('/clockings/device/shifts', {
        password,
        siteClockingDeviceId
      });
      dispatch(slice.actions.getClockingDeviceShifts(result));
    } catch (err) {
      dispatch(slice.actions.hasError(err));
      throw err;
    } finally {
      dispatch(slice.actions.setPassword(null));
      dispatch(slice.actions.stopLoading({ loadingKey: 'isLoadingClockingCode' }));
    }
  };
}
